import { useState, useEffect, useCallback } from "react";
import { GOLD, BLUE } from "../constants/temas";
import { getPlano, proximoPlano, PLANOS_CONFIG, ORDEM_PLANOS } from "../constants/planos";

const API = process.env.REACT_APP_API_URL || "https://adorateam-api-532602191494.us-central1.run.app";

function token() { return localStorage.getItem("adorateam_token"); }

async function api(method, path, body) {
    const r = await fetch(`${API}${path}`, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: body ? JSON.stringify(body) : undefined,
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data.erro || "Erro");
    return data;
}

const PERMISSOES_LABELS = [
    { key: "can_add_songs",         label: "Adicionar músicas",     emoji: "➕" },
    { key: "can_edit_songs",        label: "Editar músicas",        emoji: "✏️" },
    { key: "can_delete_songs",      label: "Remover músicas",       emoji: "🗑" },
    { key: "can_manage_repertoire", label: "Gerenciar repertório",  emoji: "🎵" },
    { key: "can_manage_schedule",   label: "Organizar escalas",     emoji: "📅" },
    { key: "can_post_mural",        label: "Publicar no mural",     emoji: "📢" },
    { key: "can_manage_members",    label: "Gerenciar membros",     emoji: "👥" },
    { key: "can_edit_group_colors", label: "Editar cores do grupo", emoji: "🎨" },
];

function BarraUso({ atual, max, cor }) {
    const ilimitado = max >= 999999;
    const pct = ilimitado ? 0 : Math.min((atual / max) * 100, 100);
    const critico = !ilimitado && pct >= 90;
    const quase = !ilimitado && pct >= 70;
    const corBarra = critico ? "#f44336" : quase ? GOLD : cor;
    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <div style={{ fontSize: 12, color: "#8899bb" }}>Membros no grupo</div>
                <div style={{ fontSize: 13, fontWeight: 800, color: critico ? "#f44336" : GOLD }}>
                    {atual} / {ilimitado ? "∞" : max}
                </div>
            </div>
            {!ilimitado && (
                <div style={{ height: 6, background: "#2a3a5e", borderRadius: 3, overflow: "hidden", marginBottom: 4 }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: `linear-gradient(90deg, ${corBarra}, ${corBarra}cc)`, borderRadius: 3, transition: "width 0.6s ease" }} />
                </div>
            )}
            {ilimitado && <div style={{ fontSize: 11, color: "#4caf50" }}>♾️ Plano ilimitado</div>}
            {critico && <div style={{ fontSize: 11, color: "#f44336", marginTop: 2 }}>⚠️ Limite quase atingido</div>}
        </div>
    );
}

export default function PainelLider({ usuario, onVoltar, onUpgrade }) {
    const [grupo, setGrupo] = useState(null);
    const [membros, setMembros] = useState([]);
    const [loading, setLoading] = useState(true);
    const [membroSel, setMembroSel] = useState(null);
    const [permissoes, setPermissoes] = useState({});
    const [salvando, setSalvando] = useState(false);
    const [copiado, setCopiado] = useState(false);
    const [tab, setTab] = useState("membros");
    const [nomeGrupo, setNomeGrupo] = useState("");
    const [igrejaGrupo, setIgrejaGrupo] = useState("");
    const [mensagem, setMensagem] = useState("");

    const carregar = useCallback(async () => {
        try {
            const res = await api("GET", "/api/grupos/meu");
            // CORREÇÃO: log de debug + fallback para não sobrescrever com vazio
            console.log("[PainelLider] resposta /api/grupos/meu:", res);
            setGrupo(res);
            setMembros(res.membros || []);
            setNomeGrupo(prev => res.nome || prev || "");
            setIgrejaGrupo(prev => res.igreja || prev || "");
        } catch (e) {
            console.error("[PainelLider] erro ao carregar grupo:", e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    const abrirPermissoes = (membro) => {
        setMembroSel(membro);
        setPermissoes({
            can_add_songs:         !!membro.can_add_songs,
            can_edit_songs:        !!membro.can_edit_songs,
            can_delete_songs:      !!membro.can_delete_songs,
            can_manage_repertoire: !!membro.can_manage_repertoire,
            can_manage_schedule:   !!membro.can_manage_schedule,
            can_post_mural:        !!membro.can_post_mural,
            can_manage_members:    !!membro.can_manage_members,
            can_edit_group_colors: !!membro.can_edit_group_colors,
        });
    };

    const salvarPermissoes = async () => {
        setSalvando(true);
        try {
            await api("PUT", `/api/grupos/permissoes/${membroSel.id}`, permissoes);
            setMembros(prev => prev.map(m => m.id === membroSel.id ? { ...m, ...permissoes } : m));
            setMembroSel(null);
            mostrarMensagem("Permissões salvas! ✓");
        } catch (e) {
            mostrarMensagem("Erro ao salvar: " + e.message);
        } finally {
            setSalvando(false);
        }
    };

    const removerMembro = async (id, nome) => {
        if (!window.confirm(`Remover ${nome} do grupo?`)) return;
        try {
            await api("DELETE", `/api/grupos/membro/${id}`);
            setMembros(prev => prev.filter(m => m.id !== id));
            mostrarMensagem(`${nome} removido do grupo`);
        } catch (e) {
            mostrarMensagem("Erro: " + e.message);
        }
    };

    const regenerarCodigo = async () => {
        if (!window.confirm("Gerar novo código? O código atual deixará de funcionar.")) return;
        try {
            const res = await api("POST", "/api/grupos/regenerar-codigo");
            setGrupo(prev => ({ ...prev, invite_code: res.invite_code }));
            mostrarMensagem("Novo código gerado! ✓");
        } catch (e) {
            mostrarMensagem("Erro: " + e.message);
        }
    };

    const salvarInfo = async () => {
        setSalvando(true);
        try {
            await api("PUT", "/api/grupos/info", { nome: nomeGrupo, igreja: igrejaGrupo });
            setGrupo(prev => ({ ...prev, nome: nomeGrupo, igreja: igrejaGrupo }));
            mostrarMensagem("Informações atualizadas! ✓");
        } catch (e) {
            mostrarMensagem("Erro: " + e.message);
        } finally {
            setSalvando(false);
        }
    };

    const copiarCodigo = () => {
        navigator.clipboard.writeText(grupo?.invite_code || "");
        setCopiado(true);
        setTimeout(() => setCopiado(false), 2000);
    };

    const mostrarMensagem = (msg) => {
        setMensagem(msg);
        setTimeout(() => setMensagem(""), 3000);
    };

    const membrosComuns = membros.filter(m => m.role !== "lider");
    const planoId = grupo?.plano || "starter";
    const planoObj = getPlano(planoId);
    const proxPlano = proximoPlano(planoId);
    const maxMembros = grupo?.max_membros || planoObj?.maxMembros || 10;
    const ilimitado = maxMembros >= 999999;
    const limiteAtingido = !ilimitado && membrosComuns.length >= maxMembros;

    const COR_PLANO = { starter: "#4CAF50", pro: GOLD, premium: "#9C27B0" };
    const corPlano = COR_PLANO[planoId] || GOLD;

    if (loading) return (
        <div style={{ minHeight: "100vh", background: BLUE, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ color: "#8899bb", fontSize: 14 }}>Carregando...</div>
        </div>
    );

    return (
        <div style={{ minHeight: "100vh", background: BLUE, paddingBottom: 40 }}>
            <style>{`@keyframes slideIn{from{opacity:0;transform:translateY(-10px)}to{opacity:1;transform:translateY(0)}}`}</style>

            {/* Header */}
            <div style={{ background: "#162040", borderBottom: "1px solid #2a3a5e", padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 10 }}>
                <div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "white" }}>⚡ Painel do Líder</div>
                    <div style={{ fontSize: 12, color: "#8899bb" }}>{grupo?.nome || "Carregando..."}</div>
                </div>
                <button onClick={onVoltar} style={{ background: "none", border: "1.5px solid #2a3a5e", borderRadius: 20, padding: "6px 14px", color: "#8899bb", fontSize: 13, cursor: "pointer", fontWeight: 600 }}>← Voltar</button>
            </div>

            {/* Toast */}
            {mensagem && (
                <div style={{ animation: "slideIn 0.3s ease", position: "fixed", top: 70, left: "50%", transform: "translateX(-50%)", background: "#162040", border: `1px solid ${GOLD}44`, borderRadius: 12, padding: "10px 20px", color: GOLD, fontSize: 13, fontWeight: 700, zIndex: 99, boxShadow: "0 4px 20px #0008" }}>{mensagem}</div>
            )}

            <div style={{ padding: "20px 16px", maxWidth: 500, margin: "0 auto" }}>

                {/* Card de plano */}
                <div style={{ background: "#162040", borderRadius: 20, padding: 20, border: `2px solid ${corPlano}44`, marginBottom: 20, boxShadow: `0 4px 24px ${corPlano}15` }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ width: 42, height: 42, borderRadius: 12, background: `${corPlano}22`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>{planoObj?.emoji}</div>
                            <div>
                                <div style={{ fontSize: 16, fontWeight: 800, color: "white" }}>Plano {planoObj?.nome}</div>
                                <div style={{ fontSize: 12, color: "#8899bb" }}>{planoObj?.preco === 0 ? "Gratuito" : `${planoObj?.precoFormatado}/mês`}</div>
                            </div>
                        </div>
                        {proxPlano && (
                            <button onClick={onUpgrade} style={{ padding: "7px 14px", borderRadius: 10, background: `${proxPlano.cor}22`, border: `1px solid ${proxPlano.cor}55`, color: proxPlano.cor, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                                ↑ {proxPlano.nome}
                            </button>
                        )}
                    </div>
                    <BarraUso atual={membrosComuns.length} max={maxMembros} cor={corPlano} />
                    {limiteAtingido && (
                        <div style={{ marginTop: 12, padding: "10px 14px", background: "#f4433615", borderRadius: 10, border: "1px solid #f4433644", fontSize: 12, color: "#ff6b6b" }}>
                            🚫 Limite atingido. Faça upgrade para <strong style={{ color: proxPlano?.cor }}>{proxPlano?.nome}</strong>
                            {proxPlano && <button onClick={onUpgrade} style={{ marginLeft: 8, background: proxPlano.cor, border: "none", borderRadius: 6, padding: "2px 10px", color: BLUE, fontSize: 11, fontWeight: 800, cursor: "pointer" }}>Fazer upgrade →</button>}
                        </div>
                    )}
                </div>

                {/* Abas */}
                <div style={{ display: "flex", background: "#0f1729", borderRadius: 14, padding: 4, marginBottom: 20, border: "1px solid #2a3a5e" }}>
                    {[["membros", "👥 Membros"], ["codigo", "🔗 Convite"], ["info", "⚙️ Grupo"]].map(([id, label]) => (
                        <button key={id} onClick={() => setTab(id)} style={{ flex: 1, padding: "9px 4px", borderRadius: 11, border: "none", background: tab === id ? GOLD : "transparent", color: tab === id ? BLUE : "#8899bb", fontWeight: tab === id ? 800 : 500, fontSize: 12, cursor: "pointer", transition: "all 0.2s" }}>{label}</button>
                    ))}
                </div>

                {/* Aba membros */}
                {tab === "membros" && (
                    <div>
                        {membrosComuns.length === 0 ? (
                            <div style={{ textAlign: "center", padding: "40px 20px", background: "#162040", borderRadius: 20, border: "1px solid #2a3a5e" }}>
                                <div style={{ fontSize: 40, marginBottom: 12 }}>👥</div>
                                <div style={{ fontSize: 16, fontWeight: 700, color: "white", marginBottom: 4 }}>Nenhum membro ainda</div>
                                <div style={{ fontSize: 13, color: "#8899bb" }}>Compartilhe o código de convite para adicionar membros</div>
                            </div>
                        ) : (
                            membrosComuns.map(m => {
                                const qtdPerm = PERMISSOES_LABELS.filter(p => m[p.key]).length;
                                const fotoSrc = m.foto || m.foto_url || null;
                                return (
                                    <div key={m.id} style={{ background: "#162040", borderRadius: 16, padding: "14px 16px", marginBottom: 12, border: "1px solid #2a3a5e" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                            <div style={{ width: 44, height: 44, borderRadius: 14, background: `${GOLD}22`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0, overflow: "hidden" }}>
                                                {fotoSrc
                                                    ? <img src={fotoSrc} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                                    : <span style={{ color: GOLD, fontWeight: 900, fontSize: 18 }}>{m.nome?.[0]?.toUpperCase()}</span>}
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: 700, color: "white", fontSize: 15 }}>{m.nome}</div>
                                                <div style={{ fontSize: 12, color: "#8899bb" }}>{m.email}</div>
                                                <div style={{ fontSize: 11, marginTop: 2 }}>
                                                    {qtdPerm === 0
                                                        ? <span style={{ color: "#4a5a7e" }}>Sem permissões extras</span>
                                                        : <span style={{ color: GOLD }}>{qtdPerm} permissão{qtdPerm !== 1 ? "ões" : ""}</span>}
                                                </div>
                                            </div>
                                            <div style={{ display: "flex", gap: 6 }}>
                                                <button onClick={() => abrirPermissoes(m)} style={{ background: `${GOLD}15`, border: `1px solid ${GOLD}33`, borderRadius: 10, padding: "6px 10px", color: GOLD, fontSize: 13, cursor: "pointer", fontWeight: 700 }}>⚙️</button>
                                                <button onClick={() => removerMembro(m.id, m.nome)} style={{ background: "#f4433610", border: "1px solid #f4433630", borderRadius: 10, padding: "6px 10px", color: "#ff6b6b", fontSize: 13, cursor: "pointer" }}>🗑</button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}

                {/* Aba código */}
                {tab === "codigo" && (
                    <div>
                        <div style={{ background: "#162040", borderRadius: 20, padding: 24, border: "1px solid #2a3a5e", marginBottom: 16 }}>
                            <div style={{ textAlign: "center", marginBottom: 20 }}>
                                <div style={{ fontSize: 13, color: "#8899bb", marginBottom: 12, fontWeight: 600 }}>CÓDIGO DE CONVITE</div>
                                <div style={{ fontSize: 36, fontWeight: 900, color: GOLD, letterSpacing: 6, background: `${GOLD}10`, borderRadius: 14, padding: "16px 20px", border: `2px dashed ${GOLD}44` }}>{grupo?.invite_code}</div>
                            </div>
                            <div style={{ display: "flex", gap: 10 }}>
                                <button onClick={copiarCodigo} style={{ flex: 1, padding: 14, borderRadius: 14, background: copiado ? "#4caf5022" : `linear-gradient(135deg, ${GOLD}, #FFD166)`, border: copiado ? "1px solid #4caf5044" : "none", color: copiado ? "#4caf50" : BLUE, fontWeight: 800, fontSize: 14, cursor: "pointer", transition: "all 0.3s" }}>{copiado ? "✓ Copiado!" : "📋 Copiar código"}</button>
                                <button onClick={() => { const url = `${window.location.origin}/convite/${grupo?.invite_code}`; navigator.clipboard.writeText(url); mostrarMensagem("Link copiado! ✓"); }} style={{ padding: "14px 16px", borderRadius: 14, background: "#0f1729", border: "1.5px solid #2a3a5e", color: "white", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>🔗 Link</button>
                            </div>
                        </div>
                        {limiteAtingido && (
                            <div style={{ background: "#f4433615", borderRadius: 14, padding: 16, border: "1px solid #f4433644", marginBottom: 16 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: "#ff6b6b", marginBottom: 4 }}>🚫 Limite de membros atingido</div>
                                <div style={{ fontSize: 12, color: "#8899bb" }}>Novos membros não poderão entrar até você fazer upgrade.</div>
                                {proxPlano && <button onClick={onUpgrade} style={{ marginTop: 10, padding: "8px 20px", borderRadius: 10, background: proxPlano.cor, border: "none", color: BLUE, fontSize: 13, fontWeight: 800, cursor: "pointer" }}>↑ Upgrade para {proxPlano.nome}</button>}
                            </div>
                        )}
                        <div style={{ background: "#162040", borderRadius: 16, padding: 16, border: "1px solid #2a3a5e" }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: "white", marginBottom: 6 }}>Link de convite</div>
                            <div style={{ background: "#0f1729", borderRadius: 10, padding: "10px 14px", fontSize: 12, color: "#8899bb", fontFamily: "monospace", wordBreak: "break-all", marginBottom: 12 }}>{window.location.origin}/convite/{grupo?.invite_code}</div>
                            <button onClick={regenerarCodigo} style={{ width: "100%", padding: 12, borderRadius: 12, background: "none", border: "1.5px solid #f4433644", color: "#ff6b6b", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>🔄 Gerar novo código</button>
                        </div>
                    </div>
                )}

                {/* Aba info */}
                {tab === "info" && (
                    <div style={{ background: "#162040", borderRadius: 20, padding: 24, border: "1px solid #2a3a5e" }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: "white", marginBottom: 20 }}>Informações do grupo</div>
                        <div style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "#8899bb", marginBottom: 6, letterSpacing: 0.5 }}>NOME DO MINISTÉRIO</div>
                            <input value={nomeGrupo} onChange={e => setNomeGrupo(e.target.value)} autoComplete="organization" style={{ width: "100%", padding: "13px 16px", borderRadius: 12, border: "1.5px solid #2a3a5e", background: "#0f1729", color: "white", fontSize: 16, boxSizing: "border-box" }} />
                        </div>
                        <div style={{ marginBottom: 24 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "#8899bb", marginBottom: 6, letterSpacing: 0.5 }}>NOME DA IGREJA</div>
                            <input value={igrejaGrupo} onChange={e => setIgrejaGrupo(e.target.value)} autoComplete="organization" style={{ width: "100%", padding: "13px 16px", borderRadius: 12, border: "1.5px solid #2a3a5e", background: "#0f1729", color: "white", fontSize: 16, boxSizing: "border-box" }} />
                        </div>
                        <button onClick={salvarInfo} disabled={salvando} style={{ width: "100%", padding: 14, borderRadius: 14, background: `linear-gradient(135deg, ${GOLD}, #FFD166)`, border: "none", color: BLUE, fontWeight: 800, fontSize: 15, cursor: "pointer" }}>{salvando ? "Salvando..." : "Salvar alterações ✓"}</button>
                    </div>
                )}
            </div>

            {/* Modal permissões */}
            {membroSel && (
                <div style={{ position: "fixed", inset: 0, background: "#0009", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 100 }} onClick={e => e.target === e.currentTarget && setMembroSel(null)}>
                    <div style={{ background: "#162040", borderRadius: "24px 24px 0 0", padding: "24px 20px 40px", width: "100%", maxWidth: 500, animation: "slideIn 0.3s ease", maxHeight: "85vh", overflowY: "auto" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                            <div>
                                <div style={{ fontSize: 18, fontWeight: 800, color: "white" }}>Permissões</div>
                                <div style={{ fontSize: 13, color: "#8899bb" }}>{membroSel.nome}</div>
                            </div>
                            <button onClick={() => setMembroSel(null)} style={{ background: "#2a3a5e", border: "none", borderRadius: 10, padding: "6px 12px", color: "white", cursor: "pointer", fontSize: 16 }}>✕</button>
                        </div>

                        {PERMISSOES_LABELS.map(({ key, label, emoji }) => (
                            <div key={key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0", borderBottom: "1px solid #2a3a5e" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                    <span style={{ fontSize: 20 }}>{emoji}</span>
                                    <div>
                                        <span style={{ fontSize: 14, color: "white", fontWeight: 500 }}>{label}</span>
                                        {key === "can_post_mural" && (
                                            <div style={{ fontSize: 10, color: "#8899bb", marginTop: 1 }}>Permite publicar avisos para toda a equipe</div>
                                        )}
                                    </div>
                                </div>
                                <button onClick={() => setPermissoes(p => ({ ...p, [key]: !p[key] }))} style={{ width: 52, height: 28, borderRadius: 14, border: "none", background: permissoes[key] ? GOLD : "#2a3a5e", cursor: "pointer", position: "relative", transition: "background 0.2s" }}>
                                    <div style={{ width: 20, height: 20, borderRadius: "50%", background: "white", position: "absolute", top: 4, left: permissoes[key] ? 28 : 4, transition: "left 0.2s", boxShadow: "0 1px 4px #0004" }} />
                                </button>
                            </div>
                        ))}

                        <button onClick={salvarPermissoes} disabled={salvando} style={{ width: "100%", marginTop: 20, padding: 15, borderRadius: 14, background: `linear-gradient(135deg, ${GOLD}, #FFD166)`, border: "none", color: BLUE, fontWeight: 800, fontSize: 16, cursor: "pointer" }}>{salvando ? "Salvando..." : "Salvar permissões ✓"}</button>
                    </div>
                </div>
            )}
        </div>
    );
}